<template>
  <aside class="app-sidebar" :class="{ 'app-sidebar--collapsed': collapsed }">
    <div class="app-sidebar__brand">
      <router-link to="/dashboard" class="app-sidebar__brand-link" aria-label="Ir al inicio">
        <img :src="logo" class="app-sidebar__logo" alt="DocBrain">
        <span v-if="!collapsed" class="app-sidebar__brand-text">
          <strong>DocBrain</strong>
          <small>Gestión inteligente</small>
        </span>
      </router-link>
    </div>

    <nav class="app-sidebar__nav" aria-label="Navegación principal">
      <template v-for="(group, groupIndex) in groups" :key="group.title || 'principal'">
        <div v-if="!group.title || collapsed" class="app-sidebar__group">
          <span v-if="group.title && !collapsed" class="app-sidebar__group-title">{{ group.title }}</span>
          <router-link
            v-for="item in group.items"
            :key="item.route"
            class="app-sidebar__link"
            :to="item.route"
            :title="collapsed ? item.label : undefined"
            :aria-label="collapsed ? item.label : undefined"
            :aria-current="isActive(item.route) ? 'page' : undefined"
          >
            <i :class="item.icon" aria-hidden="true"></i>
            <span v-if="!collapsed">{{ item.label }}</span>
          </router-link>
        </div>

        <div v-else class="app-sidebar__group">
          <button
            class="app-sidebar__group-toggle"
            type="button"
            :disabled="group.items.length === 0"
            :aria-expanded="openGroups.has(groupIndex)"
            @click="toggleGroup(groupIndex)"
          >
            <i v-if="group.icon" :class="group.icon" aria-hidden="true"></i>
            <span>{{ group.title }}</span>
            <i
              v-if="group.items.length > 0"
              class="fa-solid fa-chevron-down app-sidebar__group-chevron"
              :class="{ 'app-sidebar__group-chevron--open': openGroups.has(groupIndex) }"
              aria-hidden="true"
            ></i>
          </button>
          <div v-show="openGroups.has(groupIndex)" class="app-sidebar__subnav">
            <router-link
              v-for="item in group.items"
              :key="item.route"
              class="app-sidebar__link app-sidebar__link--nested"
              :to="item.route"
              :aria-current="isActive(item.route) ? 'page' : undefined"
            >
              <i :class="item.icon" aria-hidden="true"></i>
              <span>{{ item.label }}</span>
            </router-link>
          </div>
        </div>
      </template>
    </nav>

    <div class="app-sidebar__footer">
      <button
        class="app-sidebar__collapse"
        type="button"
        :aria-label="collapsed ? 'Expandir menú lateral' : 'Contraer menú lateral'"
        :title="collapsed ? 'Expandir menú' : 'Contraer menú'"
        @click="$emit('toggle')"
      >
        <i :class="collapsed ? 'fa-solid fa-angles-right' : 'fa-solid fa-angles-left'" aria-hidden="true"></i>
        <span v-if="!collapsed">Contraer menú</span>
      </button>
      <button class="app-sidebar__logout" type="button" title="Cerrar sesión" @click="$emit('logout')">
        <i class="fa-solid fa-right-from-bracket" aria-hidden="true"></i>
        <span v-if="!collapsed">Cerrar sesión</span>
      </button>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import logo from '@/assets/docbrain-logo-transparent.webp'
import type { NavigationGroup } from '@/config/navigation'

const props = defineProps<{ collapsed: boolean, groups: NavigationGroup[] }>()
defineEmits<{ (event: 'toggle'): void, (event: 'logout'): void }>()

const route = useRoute()
const openGroups = ref(new Set<number>())

const routeMatches = (path: string) =>
  route.path === path || (path !== '/dashboard' && route.path.startsWith(`${path}/`))
const activeMenuRoute = computed(() =>
  props.groups
    .flatMap((group) => group.items)
    .filter((item) => routeMatches(item.route))
    .sort((first, second) => second.route.length - first.route.length)[0]?.route
)
const isActive = (path: string) => activeMenuRoute.value === path

const openActiveGroups = () => {
  props.groups.forEach((group, index) => {
    if (group.items.some((item) => isActive(item.route))) openGroups.value.add(index)
  })
}

const toggleGroup = (index: number) => {
  if (props.groups[index].items.length === 0) return
  const next = new Set(openGroups.value)
  next.has(index) ? next.delete(index) : next.add(index)
  openGroups.value = next
}

onMounted(openActiveGroups)
watch(() => route.path, openActiveGroups)
</script>
